import { UserLocation_Base } from '~/common/typescript/models/UserLocation';
import { GeoJSONSourceSpecification } from 'maplibre-gl';
import maplibregl from 'maplibre-gl';
import { IconUtil } from '~/utils/IconUtil';
import { getHashColor } from '~/utils/hashStringUtil';
import { renderToString } from 'react-dom/server';
import { PiUser } from 'react-icons/pi';

export const MemberMapUtilOptimized = {
  _sourceId: 'members-source-optimized',
  _layerId: 'members-layer-optimized',

  // Cache management
  private_membersOnMap: new Map<number, UserLocation_Base>(),
  private_memberHashes: new Map<number, string>(),
  private_bitmapCache: new Map<string, string>(), // username -> bitmapName
  private_labelOverlays: new Map<number, HTMLDivElement>(), // memberId -> div element

  /**
   * Compute hash for a member to detect changes
   */
  computeMemberHash(member: UserLocation_Base): string {
    const hashData = {
      id: member.id,
      location: member.location,
      userId: member.user?.id,
      username: member.user?.username,
      fullname: member.user?.fullname,
    };
    return JSON.stringify(hashData);
  },

  /**
   * Generate bitmap name for a user
   */
  async getBitmapForUser(member: UserLocation_Base): Promise<string> {
    const username = member.user?.username || 'NoName';
    const fullname = member.user?.fullname || 'No Name';

    // Check cache first, but also verify the image still exists in map
    if (this.private_bitmapCache.has(username)) {
      const cachedBitmapName = this.private_bitmapCache.get(username)!;
      if (window.map.hasImage(cachedBitmapName)) {
        return cachedBitmapName;
      } else {
        // Image was removed (probably due to style reload), remove from cache
        this.private_bitmapCache.delete(username);
      }
    }

    // Generate new bitmap
    const bitmapName = `member-avatar-${username}-${Date.now()}`;
    const text = fullname.slice(0, 2);
    const { backgroundColor, foregroundColor } = getHashColor(username);
    const bitmap = await IconUtil.getTextAvatarBitmap(text, backgroundColor, foregroundColor);

    if (!window.map.hasImage(bitmapName)) {
      window.map.addImage(bitmapName, bitmap!);
    }

    // Cache the bitmap name
    this.private_bitmapCache.set(username, bitmapName);
    return bitmapName;
  },

  /**
   * Compute diff between current and new members
   */
  computeMembersDiff(newMembers: UserLocation_Base[]) {
    const newMembersMap = new Map(newMembers.map(m => [m.id!, m]));
    const currentMembersMap = new Map(Array.from(this.private_membersOnMap.entries()));

    const toAdd: UserLocation_Base[] = [];
    const toUpdate: UserLocation_Base[] = [];
    const toRemove: UserLocation_Base[] = [];

    // Check for new and updated members
    for (const member of newMembers) {
      const memberId = member.id!;
      const newHash = this.computeMemberHash(member);
      const oldHash = this.private_memberHashes.get(memberId);

      if (!this.private_membersOnMap.has(memberId)) {
        // New member
        toAdd.push(member);
      } else if (oldHash !== newHash) {
        // Updated member
        toUpdate.push(member);
      }
    }

    // Check for removed members
    for (const [memberId, member] of currentMembersMap) {
      if (!newMembersMap.has(memberId)) {
        toRemove.push(member);
      }
    }

    return { toAdd, toUpdate, toRemove };
  },

  /**
   * Update members with differential algorithm
   */
  async updateMembers(members: UserLocation_Base[], showLabels: boolean = false) {
    if (!window.map) return;

    // Initialize source if it doesn't exist
    if (!window.map.getSource(this._sourceId)) {
      const emptyGeojson: GeoJSONSourceSpecification = {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [],
        } as GeoJSON.FeatureCollection,
      };
      window.map.addSource(this._sourceId, emptyGeojson);
      window.map.addLayer({
        id: this._layerId,
        source: this._sourceId,
        type: 'symbol',
        layout: {
          'icon-image': ['get', 'bitmapName'],
          'icon-size': 1,
          'icon-allow-overlap': true,
        },
      });

      // Move member layer to top to ensure visibility over all basemap types
      this.moveLayerToTop();
    }

    // Handle label layer
    this.updateLabelLayer(showLabels);

    const { toAdd, toUpdate, toRemove } = this.computeMembersDiff(members);

    // Process removals
    for (const member of toRemove) {
      await this.removeMemberFromMap(member);
    }

    // Process additions and updates
    const membersToProcess = [...toAdd, ...toUpdate];
    if (membersToProcess.length > 0) {
      await this.addMembersToMap(membersToProcess);
    }

    // Update internal state
    this.private_membersOnMap.clear();
    this.private_memberHashes.clear();

    for (const member of members) {
      this.private_membersOnMap.set(member.id!, member);
      this.private_memberHashes.set(member.id!, this.computeMemberHash(member));
    }

    // Update source data with all current members
    await this.updateSourceData(members);

    // Ensure member layer is on top after updates
    this.moveLayerToTop();

    // Always remove existing label overlays first
    this.removeLabelOverlays();

    // Create new label overlays if showing
    if (showLabels) {
      this.createLabelOverlays();
    }
  },

  /**
   * Update the GeoJSON source data
   */
  async updateSourceData(members: UserLocation_Base[]) {
    if (!window.map) return;

    const features: GeoJSON.Feature[] = [];

    for (const member of members) {
      if (!member.location || !member.user) continue;

      try {
        const locationObj = JSON.parse(member.location);
        let geometry: GeoJSON.Geometry | null = null;

        // Handle GeoJSON format
        if (locationObj.type === "Point" && locationObj.coordinates) {
          geometry = locationObj;
        }
        // Handle lat/lng format
        else if (locationObj.latitude && locationObj.longitude) {
          geometry = {
            type: "Point",
            coordinates: [locationObj.longitude, locationObj.latitude]
          };
        }

        if (geometry) {
          const bitmapName = await this.getBitmapForUser(member);

          features.push({
            type: 'Feature',
            geometry: geometry,
            properties: {
              id: member.id,
              userId: member.user.id,
              username: member.user.username,
              fullname: member.user.fullname,
              bitmapName: bitmapName,
            },
          });
        }
      } catch (error) {
        console.error('Error processing member location:', error);
      }
    }

    const geojsonData: GeoJSONSourceSpecification = {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: features,
      } as GeoJSON.FeatureCollection,
    };

    const source = window.map.getSource(this._sourceId);
    if (source && source.type === 'geojson') {
      (source as maplibregl.GeoJSONSource).setData(geojsonData.data as GeoJSON.FeatureCollection);
    }
  },

  /**
   * Update label overlays visibility
   */
  updateLabelLayer(showLabels: boolean) {
    if (!window.map) return;

    // Always remove existing labels first
    this.removeLabelOverlays();

    // Create new labels if needed
    if (showLabels) {
      this.createLabelOverlays();
    }
  },

  /**
   * Create HTML div overlays for labels
   */
  createLabelOverlays() {
    if (!window.map) return;

    this.private_membersOnMap.forEach((member, memberId) => {
      if (!member.location || !member.user) return;

      try {
        const locationObj = JSON.parse(member.location);
        let coordinates: [number, number] | null = null;

        // Handle GeoJSON format
        if (locationObj.type === "Point" && locationObj.coordinates) {
          coordinates = locationObj.coordinates;
        }
        // Handle lat/lng format
        else if (locationObj.latitude && locationObj.longitude) {
          coordinates = [locationObj.longitude, locationObj.latitude];
        }

        if (coordinates) {
          // Create label div
          const labelDiv = document.createElement('div');
          labelDiv.className = 'member-label-overlay';
          labelDiv.innerHTML = `${renderToString(<PiUser style={{ verticalAlign: '-1px', marginRight: 4, color: '#1677ff' }} />)}${member.user.fullname || member.user.username || 'No Name'}`;
          labelDiv.style.cssText = `
            background: rgba(255, 255, 255, 0.9);
            border: 1px solid #ccc;
            border-radius: 4px;
            padding: 2px 6px;
            font-size: 12px;
            color: #333;
            white-space: nowrap;
            pointer-events: none;
            box-shadow: 0 1px 3px rgba(0,0,0,0.3);
          `;

          // Create MapLibre marker with the div
          const marker = new maplibregl.Marker({
            element: labelDiv,
            anchor: 'left',
            offset: [15, 0] // Offset to the right of the member icon
          })
          .setLngLat(coordinates)
          .addTo(window.map);

          // Store both div and marker for cleanup
          this.private_labelOverlays.set(memberId, labelDiv);
          (labelDiv as any)._marker = marker;
        }
      } catch (error) {
        console.error('Error creating member label overlay:', error);
      }
    });
  },

  /**
   * Remove all label overlays
   */
  removeLabelOverlays() {
    this.private_labelOverlays.forEach((labelDiv, memberId) => {
      const marker = (labelDiv as any)._marker;
      if (marker && marker.remove) {
        try {
          marker.remove();
        } catch (error) {
          console.warn('Error removing marker:', error);
        }
      }
      // Also remove the DOM element if it exists
      if (labelDiv && labelDiv.parentNode) {
        try {
          labelDiv.parentNode.removeChild(labelDiv);
        } catch (error) {
          console.warn('Error removing label div:', error);
        }
      }
    });
    this.private_labelOverlays.clear();
  },

  /**
   * Add members to map (placeholder - we update all via updateSourceData)
   */
  async addMembersToMap(members: UserLocation_Base[]) {
    // Implementation handled by updateSourceData
    // This method exists for API consistency with CommentMapUtilOptimized
  },

  /**
   * Remove member from map (placeholder - we update all via updateSourceData)
   */
  async removeMemberFromMap(member: UserLocation_Base) {
    // Implementation handled by updateSourceData
    // This method exists for API consistency with CommentMapUtilOptimized
  },

  /**
   * Move member layer to top for visibility over all basemap types
   */
  moveLayerToTop() {
    if (!window.map || !window.map.getLayer(this._layerId)) return;

    try {
      // Move member layer to top (no beforeId means move to top)
      window.map.moveLayer(this._layerId);
    } catch (error) {
      console.warn('Error moving member layer to top:', error);
    }
  },

  /**
   * Check if the member layer is currently visible on the map
   */
  isLayerVisible(): boolean {
    if (!window.map || !window.map.getLayer(this._layerId)) return false;
    const visibility = window.map.getLayoutProperty(this._layerId, 'visibility');
    return visibility !== 'none';
  },

  /**
   * Set visibility of the member layer
   */
  setVisible(visible: boolean): void {
    if (!window.map || !window.map.getLayer(this._layerId)) return;
    window.map.setLayoutProperty(this._layerId, 'visibility', visible ? 'visible' : 'none');

    // Also handle label overlays visibility
    if (!visible) {
      // Remove label overlays when hiding the layer
      this.removeLabelOverlays();
    }
  },

  /**
   * Remove all members from map
   */
  removeMembersFromMap(members: UserLocation_Base[]) {
    if (!window.map) return;

    // Remove label overlays
    this.removeLabelOverlays();

    // Remove layers
    if (window.map.getLayer(this._layerId)) {
      window.map.removeLayer(this._layerId);
    }

    // Remove source
    if (window.map.getSource(this._sourceId)) {
      window.map.removeSource(this._sourceId);
    }

    // Clear cache
    this.private_membersOnMap.clear();
    this.private_memberHashes.clear();
    this.private_bitmapCache.clear();
  },
};